import React, { useRef, useState, useEffect } from "react";
import { UserAvatar } from "..";
import MentionMemoEditor from "./components/MentionMemoEditor";
import {
  Box,
  Button,
  CircularProgress,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import moment from "moment";
import { useAppContext, useMentions } from "src/hooks";
import MentionDetailPopover from "./components/MentionDetailPopover";
import { wrap_image_with_anchor_and_open_in_new_tab } from "src/utils/regex";
import { Label, Iconify } from "src/components";
import ReplyComment from "./components/ReplyComment";

import { autoMarkAsReadCommentsAfterViewing } from "src/constants/constants";
// ==========================================================

function TaskComment({
  comment,
  isEditing,
  onEditClick,
  onDelComment,
  editCommentTitle,
  setEditCommentTitle,
  handleUpdateTaskComment,
  markCommentAsRead,
  selectedTask,
  replyTarget,
  setReplyTarget,
  replyValues,
  setReplyValues,
  onSubmitReply,
  getReplyList,
  editingCommentId,
  onReplyComment,
  sendingReply,
  attachments,
  projectTeam,
}) {
  const { dispatch_get_user_profile } = useAppContext();
  const {
    displayMentionName,
    handleMentionHover,
    handleMentionLeave,
    mentionAnchorEl,
    hoveredUser,
  } = useMentions(
    [
      // include task creator in task team for @mention hover
      ...(selectedTask?.team || []),
      ...(selectedTask?.creator_profile &&
      !(selectedTask?.team || []).some(
        (u) => u.user_id === selectedTask.creator_profile.user_id
      )
        ? [selectedTask.creator_profile]
        : []),
    ],
    attachments || [],
    projectTeam
  );
  const userId = dispatch_get_user_profile().user_id;
  const is_mobile_view = useMediaQuery("(max-width:600px)");

  const editorRef = useRef();
  const commentEditorRef = useRef();
  const replies = getReplyList?.(comment) || [];
  const [showAllReplies, setShowAllReplies] = useState(false);
  const canManageReply = (reply) =>
    reply?.creator?.user_id === userId ||
    String(selectedTask?.role).toLowerCase() === "admin";
  const canDeleteComment =
    comment?.creator?.user_id === userId ||
    String(selectedTask?.role).toLowerCase() === "admin";
  const replyComposerOpen = replyTarget?.commentId === comment._id;
  const isSendingReply = sendingReply?.[comment._id] || false;

  // =================== Auto mark as read ===================
  const chipRef = useRef(null);
  const isUnread = comment?.unread_users?.some((u) => u.user_id === userId);

  useEffect(() => {
    if (!isUnread || !chipRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            autoMarkAsReadCommentsAfterViewing(comment._id, markCommentAsRead);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(chipRef.current);

    return () => observer.disconnect();
  }, [isUnread, comment._id, markCommentAsRead]);
  // ========================================================
  return (
    <div key={comment._id}>
      <div
        className={is_mobile_view ? "d-flex pt-3 p-1" : "p-4 pe-0 pb-0 d-flex"}
        style={{ position: "relative" }}
      >
        {replies.length > 0 && (
          <Box
            className="comment-reply-line-vertical"
            sx={{
              position: "absolute",
              left: is_mobile_view ? "14px" : "43px",
              top: is_mobile_view ? "40px" : "64px",
              bottom: is_mobile_view ? "-26px" : "-34px",
              width: 0,
              borderLeft: "1px solid #bdbdbda1",
              boxSizing: "border-box",
            }}
          />
        )}
        <UserAvatar
          image={comment.creator.image}
          alt={comment.creator.first_name}
          name={comment.creator.first_name}
          sx={{
            width: is_mobile_view ? 24 : 40,
            height: is_mobile_view ? 24 : 40,
          }}
        />

        <div className="px-2 w-100">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <div
                className="comment-name"
                style={{ fontSize: is_mobile_view && "12px" }}
              >
                {comment.creator.first_name + " " + comment.creator.last_name}
              </div>
              {is_mobile_view ? (
                <div className="d-none"></div>
              ) : (
                <div className="comment-time">
                  {moment(comment?.created_at).format("MMM DD [at] hh:mm a")}
                </div>
              )}
            </div>
            <div className="d-flex align-items-center gap-2">
              {comment.edited_at && (
                <Tooltip
                  title={`Edited ${moment(comment?.edited_at).format(
                    "MMM DD [at] hh:mm a"
                  )}`}
                >
                  <span>
                    <Label
                      color="default"
                      variant="ghost"
                      sx={{
                        fontWeight: 600,
                        fontSize: "10px",
                        height: "20px",
                        minWidth: "20px",
                        borderRadius: "4px",
                        color: "#434a50",
                      }}
                    >
                      Edited
                    </Label>
                  </span>
                </Tooltip>
              )}
              {String(comment?.comment_type) === "1" && (
                <Tooltip title="This comment is private and only you can see it">
                  <span>
                    <Label
                      color="info"
                      variant="ghost"
                      sx={{
                        fontWeight: 600,
                        fontSize: "10px",
                        height: "20px",
                        minWidth: "20px",
                        borderRadius: "4px",
                      }}
                    >
                      Private
                    </Label>
                  </span>
                </Tooltip>
              )}
              {comment?.unread_users?.some((u) => u.user_id === userId) && (
                <Button
                  ref={chipRef}
                  size="small"
                  color="inherit"
                  variant="outlined"
                  sx={{
                    height: "20px",
                    fontSize: "10px",
                    textTransform: "none",
                    padding: "0 6px",
                    minWidth: "auto",
                    borderRadius: "4px",
                    ":hover": {
                      backgroundColor: "transparent",
                      color: "#00AB55",
                      borderColor: "#00ab553b",
                    },
                  }}
                  onClick={() => markCommentAsRead("single", comment._id)}
                >
                  Mark as read
                </Button>
              )}
            </div>
          </div>

          {!isEditing && (
            <div style={{ position: "relative" }}>
              {comment?.unread_users?.some((u) => u.user_id === userId) && (
                <div
                  style={{
                    position: "absolute",
                    top: "2px",
                    right: "2px",
                    height: "8px",
                    width: "8px",
                    backgroundColor: "#FF4842",
                    borderRadius: "50%",
                  }}
                />
              )}
              <div
                className={`comment-data mt-1 ${
                  comment?.unread_users?.some((u) => u.user_id === userId)
                    ? "unread-comment"
                    : ""
                }`}
                dangerouslySetInnerHTML={{
                  __html: displayMentionName(
                    wrap_image_with_anchor_and_open_in_new_tab(
                      comment.comment_title
                    )
                  ),
                }}
                style={{
                  wordWrap: "break-word",
                  wordBreak: "break-word",
                }}
                onMouseMove={handleMentionHover}
                onMouseLeave={handleMentionLeave}
              />
            </div>
          )}

          <MentionDetailPopover
            anchorEl={mentionAnchorEl}
            user={hoveredUser}
            onClose={handleMentionLeave}
            showDetail={true}
            taskCreatorId={selectedTask?.creator_profile?.user_id}
          />

          {isEditing && (
            <div className="mt-1">
              <MentionMemoEditor
                ref={commentEditorRef}
                editorRef={editorRef}
                value={editCommentTitle}
                team={[
                  // include task creator in task team for @mention
                  ...(selectedTask?.team || []),
                  ...(selectedTask?.creator_profile &&
                  !(selectedTask?.team || []).some(
                    (u) => u.user_id === selectedTask.creator_profile.user_id
                  )
                    ? [selectedTask.creator_profile]
                    : []),
                ]}
                mentionableTeam={
                  selectedTask?.role === "client" ||
                  selectedTask?.role === "client_team"
                    ? [
                        // include task creator in task team for @mention
                        ...(selectedTask?.team?.filter((user) =>
                          projectTeam?.some((pu) => pu.user_id === user.user_id)
                        ) || []),
                        ...(selectedTask?.creator_profile &&
                        !(
                          selectedTask?.team?.filter((user) =>
                            projectTeam?.some(
                              (pu) => pu.user_id === user.user_id
                            )
                          ) || []
                        ).some(
                          (u) =>
                            u.user_id === selectedTask.creator_profile.user_id
                        )
                          ? [selectedTask.creator_profile]
                          : []),
                      ]
                    : [
                        ...(selectedTask?.team || []),
                        ...(selectedTask?.creator_profile &&
                        !(selectedTask?.team || []).some(
                          (u) =>
                            u.user_id === selectedTask.creator_profile.user_id
                        )
                          ? [selectedTask.creator_profile]
                          : []),
                      ]
                }
                editorHeight={250}
                canMention={true}
                attachments={attachments || []}
              />
              <div className="mt-1">
                <Button
                  size="small"
                  variant="contained"
                  onClick={(e) => {
                    const val = commentEditorRef.current.getValue();
                    handleUpdateTaskComment(e, "detail", val, comment);
                  }}
                >
                  Save
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ ml: 1 }}
                  onClick={() => onEditClick({ _id: "" })}
                >
                  Discard
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="d-flex align-items-center ">
        {!isEditing && (
          <div
            className={
              is_mobile_view ? "pb-0 d-flex gap-2" : "pb-0 d-flex gap-2 mt-2"
            }
            style={{ paddingLeft: is_mobile_view ? "38px" : "73px" }}
          >
            {comment?.creator?.user_id ===
              dispatch_get_user_profile().user_id && (
              <div
                className="edit-delete-reply-button"
                onClick={() => onEditClick(comment)}
              >
                Edit
              </div>
            )}
            {canDeleteComment && (
              <div
                className="edit-delete-reply-button"
                onClick={() => onDelComment(comment)}
              >
                Delete
              </div>
            )}
            <div
              className="edit-delete-reply-button"
              onClick={() => onReplyComment?.(comment)}
            >
              Reply
            </div>
          </div>
        )}
        {is_mobile_view && (
          <div
            className="ml-auto"
            style={{
              fontSize: "12px",
              paddingRight: "15px",
            }}
          >
            {moment(comment?.created_at).format("MMM DD [at] hh:mm a")}
          </div>
        )}
      </div>

      {!!replies.length && (
        <div
          style={{
            marginLeft: is_mobile_view ? "38px" : "73px",
            marginTop: "8px",
            paddingRight: is_mobile_view ? "8px" : "0",
            // position: "relative",
          }}
        >
          <ReplyComment
            comment={comment}
            replies={showAllReplies ? replies : replies.slice(0, 3)}
            replyTarget={replyTarget}
            setReplyTarget={setReplyTarget}
            replyValues={replyValues}
            setReplyValues={setReplyValues}
            onSubmitReply={onSubmitReply}
            onEditClick={onEditClick}
            onDelComment={onDelComment}
            editingCommentId={editingCommentId}
            editCommentTitle={editCommentTitle}
            handleUpdateTaskComment={handleUpdateTaskComment}
            markCommentAsRead={markCommentAsRead}
            selectedTask={selectedTask}
            canManageReply={canManageReply}
            attachments={attachments || []}
            projectTeam={projectTeam}
          />
          {replies.length > 3 && (
            <div
              className="text-center w-100"
              style={{
                marginTop: "6px",
                display: "block",
              }}
              onClick={() => setShowAllReplies((prev) => !prev)}
            >
              {(() => {
                if (showAllReplies)
                  return (
                    <span
                      className="add-edit-reply-button"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "2px",
                      }}
                    >
                      Hide replies
                      <Iconify
                        icon="eva:chevron-up-fill"
                        sx={{ width: 16, height: 16 }}
                      />
                    </span>
                  );
                const unreadHidden = replies
                  .slice(3)
                  .filter((r) =>
                    r?.unread_users?.some((u) => u.user_id === userId)
                  ).length;
                return (
                  <span
                    className="add-edit-reply-button"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "2px",
                    }}
                  >
                    Show all {replies.length} replies
                    {unreadHidden > 0 && (
                      <span
                        style={{
                          color: "#FF4842",
                        }}
                      >
                        ({unreadHidden} unread)
                      </span>
                    )}
                    <Iconify
                      icon="eva:chevron-down-fill"
                      sx={{ width: 16, height: 16 }}
                    />
                  </span>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {replyComposerOpen && (
        <div
          style={{
            marginLeft: is_mobile_view ? "38px" : "73px",
            marginTop: "8px",
            paddingRight: is_mobile_view ? "8px" : "0",
          }}
        >
          <MentionMemoEditor
            ref={editorRef}
            value={replyValues?.[comment._id] || ""}
            setValue={(val) =>
              setReplyValues((prev) => ({ ...prev, [comment._id]: val }))
            }
            team={[
              // include task creator in task team for @mention
              ...(selectedTask?.team || []),
              ...(selectedTask?.creator_profile &&
              !(selectedTask?.team || []).some(
                (u) => u.user_id === selectedTask.creator_profile.user_id
              )
                ? [selectedTask.creator_profile]
                : []),
            ]}
            mentionableTeam={
              selectedTask?.role === "client" ||
              selectedTask?.role === "client_team"
                ? [
                    // include task creator in task team for @mention
                    ...(selectedTask?.team?.filter((user) =>
                      projectTeam?.some((pu) => pu.user_id === user.user_id)
                    ) || []),
                    ...(selectedTask?.creator_profile &&
                    !(
                      selectedTask?.team?.filter((user) =>
                        projectTeam?.some((pu) => pu.user_id === user.user_id)
                      ) || []
                    ).some(
                      (u) => u.user_id === selectedTask.creator_profile.user_id
                    )
                      ? [selectedTask.creator_profile]
                      : []),
                  ]
                : [
                    ...(selectedTask?.team || []),
                    ...(selectedTask?.creator_profile &&
                    !(selectedTask?.team || []).some(
                      (u) => u.user_id === selectedTask.creator_profile.user_id
                    )
                      ? [selectedTask.creator_profile]
                      : []),
                  ]
            }
            placeHolder="Write a reply..."
            canMention={true}
            attachments={attachments || []}
          />
          <div className="mt-2 d-flex gap-2">
            <Button
              size="small"
              variant="contained"
              disabled={isSendingReply}
              onClick={() => onSubmitReply(comment, editorRef)}
            >
              {isSendingReply ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                "Reply"
              )}
            </Button>
            <Button
              size="small"
              variant="text"
              color="inherit"
              disabled={isSendingReply}
              onClick={() => setReplyTarget({ commentId: "" })}
            >
              cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TaskComment;
